import React, { Component } from 'react';

import {
  Input,
  Progress,
  Button,
  CardImgOverlay,
  CardImg,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from 'reactstrap';

import PropTypes from 'prop-types';
import qs from 'qs';
import Cropper from 'react-easy-crop';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import _ from 'lodash';
import { getAxiosInstance } from '../../utils/appWrapper';
import withFormsyValidations from '../withFormsyValidations';
import RequiresRole from '../RequiresRole';

import BooleanInput from './BooleanInput';

const PIXELSIZELIMIT = 10000;

const bucketUrl = 'flaivy-assets.s3.amazonaws.com';
const cloudfrontBucketUrl = 'assets.flaivy.com';

const createImageFromUrl = url =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} image - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 */
async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImageFromUrl(imageSrc);

  if (image.width > PIXELSIZELIMIT || image.height > PIXELSIZELIMIT) {
    // eslint-disable-next-line no-alert
    alert(
      `Ursprungsbilden är för stor, den måste vara mindre än ${PIXELSIZELIMIT} pixlar i höjd och bredd`,
    );
    return null;
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const safeArea = Math.max(image.width, image.height) * 2;

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5,
  );
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    data,
    0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
    0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y,
  );

  return canvas.toDataURL('image/png');
}

// Important: on reize.
// If we want higher quality reize we can go two routers:
// 1. Go for 800x800 as default so we wont change as much when resizing.
// 2. Implement our own resizer as below:
//    https://stackoverflow.com/questions/2303690/resizing-an-image-in-an-html5-canvas
//
async function getResizedImg(imageSrc, size) {
  const image = await createImageFromUrl(imageSrc);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = size.width;
  canvas.height = size.height;
  ctx.drawImage(
    image,
    0,
    0,
    image.width,
    image.height,
    0,
    0,
    size.width,
    size.height,
  );

  // eslint-disable-next-line no-promise-executor-return
  const blob = await new Promise(resolve => canvas.toBlob(resolve));

  return blob;
}

function readFile(file) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

function getMd5Sum(file) {
  const reader = new FileReader();
  reader.readAsBinaryString(file);

  return new Promise((resolve, reject) => {
    reader.onload = e => {
      const contents = e.target.result;
      const hash = CryptoJS.MD5(CryptoJS.enc.Latin1.parse(contents)).toString();
      if (hash.length !== 32) {
        // eslint-disable-next-line no-console
        console.error('Failed to upload image');
        reject(e);
      }
      resolve(hash);
    };
    reader.abort = e => {
      reject(e);
    };
  });
}

class FileCropper extends Component {
  static get propTypes() {
    return {
      onChange: PropTypes.func.isRequired,
      name: PropTypes.string.isRequired,
      setValue: PropTypes.func.isRequired,
      value: PropTypes.string,
      errorMessage: PropTypes.string,
      isValid: PropTypes.bool.isRequired,
      height: PropTypes.number,
      width: PropTypes.number,
      restrictedPosition: PropTypes.bool,
      modalSize: PropTypes.string,
      modalHeight: PropTypes.string,
      fullscreen: PropTypes.bool,
    };
  }

  static get defaultProps() {
    return {
      value: undefined,
      height: 700,
      width: 700,
      errorMessage: '',
      restrictedPosition: false,
      modalSize: 'lg',
      modalHeight: '750px',
      fullscreen: false,
    };
  }

  static onError(err) {
    throw new Error(err);
  }

  constructor(props) {
    super(props);
    this.uploadImage = this.uploadImage.bind(this);
    this.cropResizeAndUpload = this.cropResizeAndUpload.bind(this);
    this.getSignedUrl = this.getSignedUrl.bind(this);
    this.onProgress = this.onProgress.bind(this);
    this.onFinish = this.onFinish.bind(this);
    this.toggle = this.toggle.bind(this);
    const { value } = this.props;
    this.state = { progress: 0, file: value, modal: false, darkMode: false };
    this.uploader = React.createRef();
  }

  onProgress(event) {
    const progress = _.round((100 * event.loaded) / (event.total || 1));
    this.setState({ progress });
  }

  onFinish({ url }) {
    const { name, onChange, setValue } = this.props;
    const cloudFrontUrl = url.replace(bucketUrl, cloudfrontBucketUrl);
    this.setState({ file: cloudFrontUrl, modal: false });
    setValue(cloudFrontUrl);
    onChange(name, cloudFrontUrl);
  }

  handleRawUpload = async image => {
    const signedUrlObj = await this.getSignedUrl(image);
    await this.uploadImage(signedUrlObj.signedUrl, image);
    this.onFinish(signedUrlObj);
  };

  uploadImage(signedUrl, file) {
    const options = {
      headers: {
        'Content-Type': 'image/png',
      },
      onUploadProgress: this.onProgress,
    };
    // must be an unintercepted axios call here
    return axios.put(signedUrl, file, options);
  }

  // too slow to interpret 'class-methods-use-this'
  // eslint-disable-next-line class-methods-use-this
  async getSignedUrl(file) {
    const hash = await getMd5Sum(file);
    const fileEnding = 'png';
    const queryStr = qs.stringify({
      fileName: `${hash}.${fileEnding}`,
      fileType: 'image/png',
    });
    const response = await getAxiosInstance().get(`/sign-s3?${queryStr}`);
    return response.data;
  }

  async cropResizeAndUpload() {
    const { image, rotation, croppedAreaPixels } = this.state;
    const { height, width } = this.props;
    const targetSize = { width, height };
    const croppedImage = await getCroppedImg(
      image,
      croppedAreaPixels,
      rotation,
    );
    if (croppedImage === null) {
      this.toggle();
      return;
    }
    const resizedImage = await getResizedImg(croppedImage, targetSize);
    const signedUrlObj = await this.getSignedUrl(resizedImage);

    await this.uploadImage(signedUrlObj.signedUrl, resizedImage);
    this.onFinish(signedUrlObj);
  }

  // resizedImage

  toggle() {
    this.setState(prevState => ({
      modal: !prevState.modal,
    }));
  }

  render() {
    const {
      file,
      progress,
      modal,
      image,
      crop,
      zoom,
      rotation,
      showRawInput,
      darkMode,
    } = this.state;
    const { uploader } = this;
    const {
      errorMessage,
      isValid,
      height,
      width,
      restrictedPosition,
      modalSize,
      modalHeight,
      value,
      fullscreen,
    } = this.props;

    return (
      <>
        <div>
          <div>
            <Modal
              isOpen={modal}
              toggle={this.toggle}
              size={modalSize}
              fullscreen={fullscreen}
            >
              <ModalHeader toggle={this.toggle}>Justera bild</ModalHeader>
              <ModalBody style={{ height: modalHeight }}>
                <Cropper
                  image={image}
                  crop={crop}
                  cropSize={{ width, height }}
                  rotation={rotation}
                  zoom={zoom}
                  zoomSpeed={0.05}
                  aspect={1}
                  restrictPosition={restrictedPosition}
                  minZoom={0.5}
                  onCropChange={c => this.setState({ crop: c })}
                  onRotationChange={r => this.setState({ rotation: r })}
                  onCropComplete={(a, ca) =>
                    this.setState({ croppedAreaPixels: ca })
                  }
                  onZoomChange={z => this.setState({ zoom: z })}
                  style={{
                    containerStyle: {
                      background: darkMode ? 'black' : 'white',
                    },
                  }}
                />
              </ModalBody>
              <ModalFooter>
                <Label>
                  Förhandsvisa i mörkt läge
                  <BooleanInput
                    name="darkMode"
                    onChange={() =>
                      this.setState({
                        ...this.state,
                        darkMode: !this.state.darkMode,
                      })
                    }
                  />
                </Label>
                <Button
                  className="float-end m-1"
                  color="primary"
                  onClick={this.cropResizeAndUpload}
                >
                  Ok
                </Button>
              </ModalFooter>
            </Modal>
          </div>
          <Card
            inverse
            style={{ maxWidth: '400px', minHeight: '100px' }}
            className={!isValid ? 'border border-danger text-with' : ''}
          >
            <CardImg src={file || value} />
            <CardImgOverlay>
              <h4>{errorMessage}</h4>
              {progress !== 0 && progress !== 100 && (
                <Progress value={progress || 0} />
              )}
              <div className="text-end">
                <Button
                  className="float-end m-1"
                  color="primary"
                  onClick={() => {
                    uploader.current && uploader.current.click();
                  }}
                >
                  <input
                    ref={uploader}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async e => {
                      const files = e.target.files;
                      if (files && files.length > 0) {
                        const filesize = files[0].size / 1024 / 1024; // MB

                        if (filesize > 12) {
                          // eslint-disable-next-line no-alert
                          await alert(
                            'Den angivna filen för stor, prova med en annan fil',
                          );
                          return;
                        }

                        const imageDataUrl = await readFile(files[0]);
                        this.setState({
                          image: imageDataUrl,
                          crop: { x: 0, y: 0 },
                          zoom: 1,
                          modal: true,
                        });
                      }
                    }}
                  />
                  Ladda upp bild
                </Button>
                {file && (
                  <Button
                    className="float-end m-1"
                    color="secondary"
                    onClick={() => {
                      this.setState({
                        image: file,
                        crop: { x: 0, y: 0 },
                        zoom: 1,
                        modal: true,
                      });
                    }}
                  >
                    Beskär bild
                  </Button>
                )}
                <RequiresRole requiredRoles={['super']}>
                  <input
                    id="image-file"
                    type="file"
                    onChange={async e => {
                      if (e.target.files && e.target.files.length > 0) {
                        await this.handleRawUpload(e.target.files[0]);
                      }
                    }}
                  />

                  {!showRawInput && (
                    <button
                      type="button"
                      onClick={() => {
                        this.setState({
                          showRawInput: true,
                        });
                      }}
                      className="btn btn-link float-end text-muted"
                    >
                      <i className="mdi mdi-square-edit-outline me-1" />
                    </button>
                  )}
                  {showRawInput && (
                    <Input
                      className="float-end m-1"
                      color="secondary"
                      value={value}
                      placeholder="url"
                      onChange={event => {
                        this.onFinish({ url: event.currentTarget.value });
                      }}
                    />
                  )}
                </RequiresRole>
              </div>
            </CardImgOverlay>
          </Card>
        </div>
      </>
    );
  }
}
export default withFormsyValidations(FileCropper);
